import { dataSourceRegistry } from './dataSourceRegistry.js';
import { polygonApi } from '../../../services/polygon.js';
import { z } from 'zod';

// Initialize data sources for the Data Fetcher Widget
let initialized = false;

// Auto-initialize when module loads
if (typeof window !== 'undefined') {
  setTimeout(() => {
    if (!initialized) {
      initializeDataSources();
    }
  }, 0);
}

export function initializeDataSources() {
  // Prevent double initialization
  if (initialized) {
    console.log('Data sources already initialized');
    return dataSourceRegistry.getAllSources();
  }
  
  console.log('Initializing data sources...');
  
  // Polygon Quotes Data Source
  dataSourceRegistry.register('polygon/quotes', {
  type: 'api',
  endpoint: 'polygon',
  authentication: 'apiKey',
  schema: z.object({
    symbols: z.array(z.string()).or(z.string().transform(s => s.split(',').map(sym => sym.trim()))),
  }),
  rateLimit: { requests: 100, period: '1m' },
  timeout: 30000,
  fetcher: async (params: { symbols: string[] }) => {
    const symbols = Array.isArray(params.symbols) ? params.symbols : [params.symbols];
    const data = await polygonApi.getBatchCombinedData(symbols, 1);
    return data.map(item => ({
      symbol: item.symbol,
      quote: item.quote,
      companyInfo: item.companyInfo,
      historicalData: item.historicalData,
    }));
  },
  cacheConfig: {
    ttl: 60000, // 1 minute
    maxSize: 100,
    staleWhileRevalidate: true,
  },
  });

  // Market Sentiment Data Source (Mock for testing)
  dataSourceRegistry.register('internal/market_sentiment', {
  type: 'api',
  endpoint: 'internal',
  authentication: 'none',
  schema: z.object({
    period: z.enum(['1d', '1w', '1m']).default('1d'),
  }),
  timeout: 5000,
  fetcher: async (params: { period: string }) => {
    // Mock data for testing
    return {
      period: params.period,
      sentiment: 'neutral',
      score: 0.52,
      bullishCount: 45,
      bearishCount: 42,
      neutralCount: 13,
      topBullish: ['AAPL', 'TSLA', 'NVDA'],
      topBearish: ['META', 'AMZN'],
      timestamp: new Date().toISOString(),
    };
  },
  cacheConfig: {
    ttl: 300000, // 5 minutes
    maxSize: 50,
  },
  });

  // Historical Data Source
  dataSourceRegistry.register('polygon/historical', {
  type: 'api',
  endpoint: 'polygon',
  authentication: 'apiKey',
  schema: z.object({
    symbol: z.string(),
    days: z.number().min(1).max(365).default(30),
  }),
  rateLimit: { requests: 100, period: '1m' },
  timeout: 30000,
  fetcher: async (params: { symbol: string; days: number }) => {
    const data = await polygonApi.getHistoricalData(params.symbol, params.days);
    return {
      symbol: params.symbol,
      days: params.days,
      data: data,
      stats: {
        high: Math.max(...data.map(d => d.high)),
        low: Math.min(...data.map(d => d.low)),
        avgVolume: data.reduce((sum, d) => sum + d.volume, 0) / data.length,
      },
    };
  },
  cacheConfig: {
    ttl: 300000, // 5 minutes
    maxSize: 100,
    staleWhileRevalidate: true,
  },
  });

  // Company Info Data Source
  dataSourceRegistry.register('polygon/company', {
  type: 'api',
  endpoint: 'polygon',
  authentication: 'apiKey',
  schema: z.object({
    symbol: z.string(),
  }),
  rateLimit: { requests: 100, period: '1m' },
  timeout: 15000,
  fetcher: async (params: { symbol: string }) => {
    const info = await polygonApi.getCompanyInfo(params.symbol);
    return info;
  },
  cacheConfig: {
    ttl: 86400000, // 24 hours (company info rarely changes)
    maxSize: 200,
  },
  });

  // Mark as initialized
  initialized = true;
  
  const sources = dataSourceRegistry.getAllSources();
  console.log('Data sources initialized:', sources);
  
  // Make registry available globally for debugging
  if (typeof window !== 'undefined') {
    (window as any).dataSourceRegistry = dataSourceRegistry;
  }
  
  return sources;
}